import { FieldType } from "@/enums";
import { faUpload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Mentions,
  Select,
  Slider,
  Switch,
  Upload,
} from "antd";
import { useIntl } from "react-intl";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const useFieldTypes = () => {
  const intl = useIntl();

  const getTitle = (fieldType) =>
    intl.formatMessage({ id: `enum.fieldType.${fieldType.toLowerCase()}` });

  return [
    {
      title: getTitle(FieldType.Text),
      fieldType: FieldType.Text,
      component: <Input placeholder="Text" tabIndex={-1} />,
    },
    {
      title: getTitle(FieldType.TextArea),
      fieldType: FieldType.TextArea,
      component: <TextArea placeholder="Longer Text" tabIndex={-1} />,
    },
    {
      title: getTitle(FieldType.TextAreaWithMentions),
      fieldType: FieldType.TextAreaWithMentions,
      component: (
        <Mentions
          placeholder="Supports @Suzy_Sawzall to tag users"
          tabIndex={-1}
          rows={2}
        />
      ),
    },
    {
      title: getTitle(FieldType.Number),
      fieldType: FieldType.Number,
      component: <InputNumber placeholder="Number" tabIndex={-1} />,
    },
    {
      title: getTitle(FieldType.Checkbox),
      fieldType: FieldType.Checkbox,
      component: (
        <Checkbox.Group
          style={{ display: "flex", flexDirection: "column" }}
          options={["Option 1", "Option 2", "Option 3", "Option 4"].map(
            (option) => ({ label: option, value: option })
          )}
          value={["Option 1", "Option 4"]}
        />
      ),
    },
    {
      title: getTitle(FieldType.DropDown),
      fieldType: FieldType.DropDown,
      component: (
        <Select
          defaultValue="Option 1"
          style={{ width: 200 }}
          open
          getPopupContainer={() =>
            document.getElementById(`tile-${FieldType.DropDown}`)
          }
          styles={{ popup: { root: { zIndex: -1 } } }}
          tabIndex={-1}
        >
          {["Option 1", "Option 2"].map((option, index) => (
            <Select.Option
              key={`dropdown-${index}`}
              value={option}
              tabIndex={-1}
            >
              {option}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: getTitle(FieldType.MultiSelectDropDown),
      fieldType: FieldType.MultiSelectDropDown,
      component: (
        <Select
          mode="multiple"
          defaultValue={["Option 1"]}
          style={{ width: "100%" }}
          open
          getPopupContainer={() =>
            document.getElementById(`tile-${FieldType.MultiSelectDropDown}`)
          }
          styles={{
            popup: {
              root: {
                zIndex: -1,
                minWidth: 230,
                width: 230,
                maxWidth: 230,
              },
            },
          }}
          tabIndex={-1}
        >
          {["Option 1", "Option 2"].map((option, index) => (
            <Select.Option key={`dropdown-${index}`} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: getTitle(FieldType.AutoComplete),
      fieldType: FieldType.AutoComplete,
      component: (
        <Select
          defaultValue="Option 1"
          style={{ width: 200 }}
          open
          getPopupContainer={() =>
            document.getElementById(`tile-${FieldType.AutoComplete}`)
          }
          styles={{ popup: { root: { zIndex: -1 } } }}
          tabIndex={-1}
        >
          {["External Option 1", "External Option 2"].map((option, index) => (
            <Select.Option
              key={`dropdown-${index}`}
              value={option}
              tabIndex={-1}
            >
              {option}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: getTitle(FieldType.MultiSelectAutoComplete),
      fieldType: FieldType.MultiSelectAutoComplete,
      component: (
        <Select
          mode="multiple"
          defaultValue={["Option 1"]}
          style={{ width: "100%" }}
          open
          getPopupContainer={() =>
            document.getElementById(`tile-${FieldType.MultiSelectAutoComplete}`)
          }
          styles={{
            popup: {
              root: {
                zIndex: -1,
                minWidth: 230,
                width: 230,
                maxWidth: 230,
              },
            },
          }}
          tabIndex={-1}
        >
          {["External Option 1", "External Option 2"].map((option, index) => (
            <Select.Option key={`dropdown-${index}`} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: getTitle(FieldType.Date),
      fieldType: FieldType.Date,
      component: <DatePicker tabIndex={-1} />,
    },
    {
      title: getTitle(FieldType.DateRange),
      fieldType: FieldType.DateRange,
      component: <RangePicker tabIndex={-1} />,
    },
    {
      title: getTitle(FieldType.PercentCompleteSlider),
      fieldType: FieldType.PercentCompleteSlider,
      component: <Slider defaultValue={30} tabIndex={-1} />,
    },
    {
      title: getTitle(FieldType.Attachment),
      fieldType: FieldType.Attachment,
      component: (
        <Upload
          fileList={[{ uid: 1, name: "file.txt" }]}
          listType="picture"
          tabIndex={-1}
        >
          <Button
            data-test-id="use-field-types-upload-single-attachment-button"
            icon={<FontAwesomeIcon icon={faUpload} />}
          >
            {intl.formatMessage({ id: "general.uploadFile" })}
          </Button>
        </Upload>
      ),
    },
    {
      title: getTitle(FieldType.MultiAttachment),
      fieldType: FieldType.MultiAttachment,
      component: (
        <div style={{ overflow: "hidden" }}>
          <Upload
            fileList={[
              { uid: 1, name: "file.txt" },
              { uid: 2, name: "file2.txt" },
            ]}
            listType="picture"
            tabIndex={-1}
          >
            <Button
              data-test-id="use-field-types-upload-multiple-attachments-button"
              icon={<FontAwesomeIcon icon={faUpload} />}
            >
              {intl.formatMessage({ id: "general.uploadFile" })}
            </Button>
          </Upload>
        </div>
      ),
    },
    {
      title: getTitle(FieldType.Switch),
      fieldType: FieldType.Switch,
      component: (
        <Switch
          disabled={true}
          defaultChecked
          tabIndex={-1}
          style={{ width: "44px" }}
        />
      ),
    },
  ];
};

export default useFieldTypes;
